<template>
  <div>
    <!-- Show upload area -->
    <div
      class="image-picker"
      :class="{ dragging: isDragging, loading: uploadLoading }"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
      @click="$refs.fileInput.click()"
    >
      <v-icon size="48" color="grey">mdi-cloud-upload</v-icon>
      <p>{{ uploadText }}</p>
      <v-progress-circular v-if="uploadLoading" indeterminate color="primary"></v-progress-circular>
    </div>

    <input ref="fileInput" type="file" :accept="acceptedTypes" @change="uploadFile" style="display: none" />
  </div>
</template>

<script>
import api from '@/assets/api'

export default {
  name: 'ImagePicker',
  props: {
    maxImages: {
      type: Number,
      default: Infinity,
    },
    folder: {
      type: String,
      default: '',
    },
    filename: {
      type: String,
      default: '',
    },
    acceptedTypes: {
      type: String,
      default: 'image/*',
    },
    addFileExtension: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      uploadLoading: false,
      uploadedCount: 0,
    }
  },
  computed: {
    uploadText() {
      return this.acceptedTypes === 'image/*'
        ? 'Drag & drop an image or click to select'
        : 'Drag & drop a file or click to select'
    },
    isImageType() {
      return this.acceptedTypes === 'image/*' || this.acceptedTypes.includes('image/')
    },
  },
  methods: {
    onDragOver() {
      this.isDragging = true
    },
    onDragLeave() {
      this.isDragging = false
    },
    onDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file && this.isValidFileType(file)) {
        this.handleFile(file)
      }
    },
    uploadFile(event) {
      const file = event.target.files[0]
      if (file) {
        this.handleFile(file)
      }
    },
    isValidFileType(file) {
      if (this.acceptedTypes === '*/*') return true
      const acceptedArray = this.acceptedTypes.split(',')
      return acceptedArray.some((type) => {
        const trimmedType = type.trim()
        if (trimmedType.endsWith('/*')) {
          return file.type.startsWith(trimmedType.slice(0, -1))
        }
        return file.type === trimmedType
      })
    },
    getFileName(file) {
      if (this.filename) {
        return this.addFileExtension ? this.appendExtension(this.filename, file.name) : this.filename
      }
      return file.name
    },
    appendExtension(filename, originalName) {
      const extension = originalName.substring(originalName.lastIndexOf('.'))
      return filename + extension
    },
    async handleFile(file) {
      if (!file) return

      this.uploadLoading = true

      try {
        // Get presigned URL
        const fileName = this.getFileName(file)
        const presignResponse = await api.apiFetch('/uploadfile', {
          method: 'POST',
          body: JSON.stringify({
            folder: this.folder,
            fileName,
            fileType: file.type,
          }),
        })

        if (!presignResponse.ok) {
          alert('Error getting upload URL')
          return
        }

        const { uploadUrl, key, viewUrl } = await presignResponse.json()

        console.log('url', uploadUrl)
        console.log('key', key)

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        })

        if (uploadResponse.ok) {
          this.uploadedCount++
          this.$emit('done', { key, viewUrl })
        } else {
          alert('Error uploading file')
        }
      } catch (error) {
        alert('Error uploading file')
      }

      this.uploadLoading = false
    },
  },
}
</script>

<style scoped>
.image-picker {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
}

.image-picker:hover {
  border-color: #1976d2;
  background-color: #f5f5f5;
}

.image-picker.dragging {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.image-picker.loading {
  opacity: 0.7;
  pointer-events: none;
}

.image-picker p {
  margin: 16px 0 0 0;
  color: #666;
}
</style>
